// A-Signal — THE combined brain. Zero AI, zero guessing.
// ═══════════════════════════════════════════════════════════════════════════
// Wires together A-Structure + A-Fibonacci + A-Regime exactly like
// JarvisSMC_Strategy.pine's entry-conditions section. This is the ONE
// function that should decide BUY/SELL/WAIT — nothing else in the app
// (including Groq/chat) should generate a trade decision.
//
//   candles (A-LivePrice, Yahoo Finance)
//         ↓
//   A-Structure   → swing trend, fibOrigin/fibExtreme
//         ↓
//   A-Fibonacci   → OTE zone (0.618-0.79), is price inside it
//         ↓
//   A-Regime      → ADX — trending or choppy
//         ↓
//   confirmation candle (last candle, engulfing/pin) — checked here
//         ↓
//   structural SL (origin ± ATR buffer) + target (fibExtreme, opposite
//   liquidity pool) + minimum R:R filter (1.5)
//         ↓
//   BUY / SELL / WAIT + Entry/SL/Target/Reason
// ═══════════════════════════════════════════════════════════════════════════

import { run as runStructure } from './A_Structure';
import { run as runFibonacci }  from './A_Fibonacci';
import { run as runRegime }     from './A_Regime';
import { JARVIS_DNA } from '../../dna/jarvisDNA';

// Wilder ATR(14) — last-value only, simple and sufficient for the SL buffer.
function calcATR(candles, period = 14) {
  const n = candles.length;
  if (n < period + 1) return null;
  const tr = [];
  for (let i = 1; i < n; i++) {
    const c = candles[i], p = candles[i - 1];
    tr.push(Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close)));
  }
  let atr = tr.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < tr.length; i++) atr = (atr * (period - 1) + tr[i]) / period;
  return atr;
}

// Confirmation candle — bullish/bearish engulfing or pin bar on the LAST
// candle. Mirrors the Pine script's bullSignal/bearSignal exactly.
function confirmationCandle(candles) {
  const n = candles.length;
  if (n < 2) return { bull: false, bear: false };
  const cur = candles[n - 1], prev = candles[n - 2];
  const bodyHi = Math.max(cur.close, cur.open);
  const bodyLo = Math.min(cur.close, cur.open);
  const range  = cur.high - cur.low;

  const bullEngulf = cur.close > cur.open && prev.close < prev.open && cur.close >= prev.open && cur.open <= prev.close;
  const bullPin    = range > 0 && (bodyLo - cur.low) >= range * 0.5 && cur.close > (cur.high + cur.low) / 2;
  const bearEngulf = cur.close < cur.open && prev.close > prev.open && cur.close <= prev.open && cur.open >= prev.close;
  const bearPin    = range > 0 && (cur.high - bodyHi) >= range * 0.5 && cur.close < (cur.high + cur.low) / 2;

  return { bull: bullEngulf || bullPin, bear: bearEngulf || bearPin };
}

// candles: array from A-LivePrice.getCandles(), oldest → newest
// htfBias: 1 (bullish) | -1 (bearish) | 0 (neutral/off) — pass from a
//          separate HTF candle fetch if you want HTF confirmation; omit to
//          skip it (useHTF effectively off).
export async function run({ candles = [], htfBias = null, swingLen, retracementOverride, adxOverride }) {
  // Read from JARVIS_DNA here, not at module top-level — see A_Fibonacci.js
  // for why (crashed the whole app in production build otherwise).
  const V = JARVIS_DNA.validated_strategy;
  if (!V) {
    // Should never happen (jarvisDNA.js is a static object) — but if it
    // ever does, fail with a CLEAR message instead of a cryptic
    // "Cannot read properties of undefined (reading 'structure')" three
    // lines down. That exact opaque crash was reported live and never
    // fully traced to a root cause — this turns it into a diagnosable one.
    console.error('[A-Signal] JARVIS_DNA.validated_strategy missing. JARVIS_DNA keys:', Object.keys(JARVIS_DNA || {}), 'trading keys:', Object.keys(JARVIS_DNA?.trading || {}));
    return { ok: false, error: 'Internal config error — validated_strategy DNA missing', agent: 'A-Signal', requiresAI: false, ts: Date.now() };
  }
  const structure = await runStructure({ candles, swingLen: swingLen || V.structure.swing_length });
  if (!structure.ok || !structure.valid) {
    return {
      ok: true, action: 'WAIT',
      reason: structure.recommendation || 'Swing structure अजून तयार नाही.',
      agent: 'A-Signal', requiresAI: false, ts: Date.now(),
    };
  }

  const currentPrice = candles[candles.length - 1].close;

  const fib = await runFibonacci({
    fibOrigin: structure.fibOrigin,
    fibExtreme: structure.fibExtreme,
    currentPrice,
    direction: structure.trend,
  });
  if (!fib.ok) {
    return { ok: true, action: 'WAIT', reason: fib.error, agent: 'A-Signal', requiresAI: false, ts: Date.now() };
  }

  // adxOverride was accepted as a param but never actually wired through —
  // dead code until now. Needed so a "study different thresholds" sweep
  // (A_Backtest.sweepADXThreshold) can actually vary this per run instead
  // of every run silently using the same default 20.
  const regime = await runRegime({ candles, threshold: adxOverride });
  if (!regime.ok) {
    return { ok: true, action: 'WAIT', reason: regime.error, agent: 'A-Signal', requiresAI: false, ts: Date.now() };
  }

  const isBull = structure.trend === 'BULLISH';

  // ── Gate 1: HTF confirmation (skip check if htfBias not supplied) ──────
  const htfOK = htfBias == null || (isBull ? htfBias === 1 : htfBias === -1);

  // ── Gate 2: ADX regime ──────────────────────────────────────────────────
  if (!regime.isTrending) {
    return {
      ok: true, action: 'WAIT',
      reason: `Choppy market (ADX ${regime.adx} ≤ ${regime.threshold}) — ${V.regime_filter.rule}`,
      structure, fib, regime,
      agent: 'A-Signal', requiresAI: false, ts: Date.now(),
    };
  }

  // ── Gate 3: HTF ───────────────────────────────────────────────────────
  if (!htfOK) {
    return {
      ok: true, action: 'WAIT',
      reason: `HTF trend मेळ खात नाही (chart=${structure.trend}, HTF bias=${htfBias}).`,
      structure, fib, regime,
      agent: 'A-Signal', requiresAI: false, ts: Date.now(),
    };
  }

  // ── Gate 4: OTE zone ─────────────────────────────────────────────────────
  if (!fib.inZone) {
    return {
      ok: true, action: 'WAIT',
      reason: `Price OTE zone (${fib.ote_zone.low}-${fib.ote_zone.high}) मध्ये अजून नाही. ${structure.trend} trend, wait करा.`,
      structure, fib, regime,
      agent: 'A-Signal', requiresAI: false, ts: Date.now(),
    };
  }

  // ── Gate 5: confirmation candle ─────────────────────────────────────────
  const confirm = confirmationCandle(candles);
  if ((isBull && !confirm.bull) || (!isBull && !confirm.bear)) {
    return {
      ok: true, action: 'WAIT',
      reason: `Price OTE zone मध्ये आहे, पण ${isBull ? 'bullish' : 'bearish'} confirmation candle अजून झाली नाही.`,
      structure, fib, regime,
      agent: 'A-Signal', requiresAI: false, ts: Date.now(),
    };
  }

  // ── Risk: structural SL + Opposite Liquidity Pool target ────────────────
  const atr = calcATR(candles, 14);
  if (!atr) {
    return { ok: true, action: 'WAIT', reason: 'ATR calculate करण्यासाठी पुरेसे candles नाहीत.', agent: 'A-Signal', requiresAI: false, ts: Date.now() };
  }

  const slBuf = atr * 1.0;  // slATRmult, validated default
  const sl     = isBull ? structure.fibOrigin - slBuf : structure.fibOrigin + slBuf;
  const target = structure.fibExtreme;   // opposite liquidity pool
  const risk   = Math.abs(currentPrice - sl);
  const reward = Math.abs(target - currentPrice);
  const rr     = risk > 0 ? reward / risk : 0;

  if (rr < V.risk.min_rr) {
    return {
      ok: true, action: 'WAIT',
      reason: `Target खूप जवळ आहे — R:R फक्त ${rr.toFixed(2)} (किमान ${V.risk.min_rr} लागतो). Trade skip.`,
      structure, fib, regime,
      agent: 'A-Signal', requiresAI: false, ts: Date.now(),
    };
  }

  const action = isBull ? 'BUY' : 'SELL';
  const mgmt = V.risk.management; // { t1_pct, move_to_be, use_trail, trail_atr_mult, rule }
  const mgmtLine = `T1 वर ${mgmt.t1_pct}% book${mgmt.move_to_be ? ', उरलेलं breakeven ला' : ''}${mgmt.use_trail ? `, मग ${mgmt.trail_atr_mult}×ATR trail` : ''}`;
  const reason = [
    `${structure.trend} structure (BOS confirmed)`,
    `price OTE zone (${fib.ote_zone.low}-${fib.ote_zone.high}) मध्ये`,
    `${isBull ? 'bullish' : 'bearish'} confirmation candle`,
    htfBias != null ? 'HTF aligned' : null,
    `ADX ${regime.adx} trending`,
    `R:R ${rr.toFixed(2)}`,
    mgmtLine,
  ].filter(Boolean).join(', ');

  return {
    ok: true,
    action,
    entry:  +currentPrice.toFixed(2),
    sl:     +sl.toFixed(2),
    target: +target.toFixed(2),
    rr:     +rr.toFixed(2),
    management: mgmt, // so the UI/Telegram can show the T1/BE/trail plan, not just a bare target
    // htfAligned was previously only baked into the human-readable `reason`
    // string ("HTF aligned") — not queryable. Self-learning needs it as a
    // real field on trades it logs, so it's a proper field here too.
    htfAligned: htfBias != null,
    reason,
    structure, fib, regime,
    agent: 'A-Signal',
    requiresAI: false,
    ts: Date.now(),
  };
}
